import { map, Observable } from 'rxjs';
import { inject } from '@angular/core';
import { NotificationBody, NotificationResponseDTO, NotificationViewModel } from '../../../interfaces';
import { NotificationsService } from '@notifications/infra';
import { NotificationStatusMapper } from '../../../mappers';
import { formattedTimeDifferenceUtil, userTrophiesMapper } from '@core';
import { NotificationType } from '../../../enums';
import { cardReasonMapper } from '@fair-play';

export interface NotificationsTabsResult {
  readonly notifications: NotificationViewModel[];
  readonly alerts: NotificationViewModel[];
}

/**
 * Wyjątek: używamy lokalnego NotificationResponseDTO zamiast typu z @core-http.
 * OpenAPI generuje NotificationDto.payload jako union type wielu różnych payload typów,
 * co wymusza używanie Record<string, unknown> i type assertions.
 * Lokalne DTO z ujednoliconym interfejsem payload jest czytelniejsze i bezpieczniejsze.
 */
export class NotificationsListQueryHandler {
  private readonly notificationsService: NotificationsService = inject(NotificationsService);

  getNotificationsTabs(): Observable<NotificationsTabsResult> {
    return this.notificationsService.notificationsList().pipe(
      map((response: NotificationResponseDTO) => ({
        notifications: (response.notificationsWithoutAlerts ?? []).map((n: NotificationBody) => this.mapToViewModel(n)),
        alerts: (response.alerts ?? []).map((n: NotificationBody) => this.mapToViewModel(n))
      }))
    );
  }

  hasUnreadNotifications(): Observable<boolean> {
    return this.getNotificationsTabs().pipe(
      map(
        ({ notifications, alerts }: NotificationsTabsResult) =>
          notifications.some((n: NotificationViewModel) => !n.isRead) ||
          alerts.some((n: NotificationViewModel) => !n.isRead)
      )
    );
  }

  private mapToViewModel(notification: NotificationBody): NotificationViewModel {
    return {
      title: this.getTitle(notification),
      description: this.getDescription(notification),
      formattedDate: this.getFormattedDate(notification),
      gameName: notification.payload.gameName,
      address: notification.payload.gameAddress,
      avatar: this.getAvatar(notification),
      isRead: notification.isRead,
      id: notification.notificationId,
      type: notification.notificationType,
      createdAt: formattedTimeDifferenceUtil(new Date(notification.createdAt)),
      gameId: notification.payload.gameId,
      displayName: notification.payload.displayName,
      customId: notification.payload.customId,
      customRedirectUrl: notification.payload.customRedirectUrl,
      teamId: notification.payload.teamId,
      teamName: notification.payload.teamName,
      receiverUserId: notification.payload.receiverUserId,
      customType: notification.payload.customType
    };
  }

  private getTitle(notification: NotificationBody): string {
    const includedInMapper: boolean = Object.values(NotificationStatusMapper).includes(notification.notificationType);

    if (
      notification.notificationType === NotificationType.CustomNotification ||
      notification.notificationType === NotificationType.UserPenaltyReceived
    )
      return notification.payload.title || NotificationStatusMapper[notification.notificationType];

    return includedInMapper ? NotificationStatusMapper[notification.notificationType] : notification.payload.title;
  }

  private getDescription(notification: NotificationBody): string {
    if (
      notification.notificationType === NotificationType.CustomNotification ||
      notification.notificationType === NotificationType.TeamMemberAddedByHost ||
      notification.notificationType === NotificationType.UserPenaltyReceived ||
      notification.notificationType === NotificationType.NewTrophyReceived ||
      notification.notificationType === NotificationType.NewCommentReceived ||
      notification.notificationType === NotificationType.GameUpdated
    )
      return notification.payload.body;

    if (notification.payload.gameName && notification.payload.gameAddress)
      return `${notification.payload.gameName} (${notification.payload.gameAddress})`;

    return notification.payload.body;
  }

  private getFormattedDate(notification: NotificationBody): string | null {
    if (
      notification.notificationType === NotificationType.CustomNotification ||
      notification.notificationType === NotificationType.TeamMemberAddedByHost ||
      notification.notificationType === NotificationType.UserPenaltyReceived ||
      notification.notificationType === NotificationType.NewTrophyReceived ||
      notification.notificationType === NotificationType.NewCommentReceived
    )
      return null;

    if (notification.payload.gameDate && notification.payload.gameHours)
      return `${notification.payload.gameDate} | ${notification.payload.gameHours}`;

    return null;
  }

  private getAvatar(notification: NotificationBody): string {
    if (notification.notificationType === NotificationType.NewTrophyReceived)
      return userTrophiesMapper[notification.payload.trophyType].icon;

    if (notification.notificationType === NotificationType.UserPenaltyReceived)
      return cardReasonMapper[notification.payload.penaltyReason].icon;

    if (notification.notificationType === NotificationType.NewCommentReceived) return 'assets/icons/comment-icon.svg';

    return notification.payload.thumbnailUrl;
  }
}
